import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { t } from './i18n';

/**
 * Reusable BI report PDF generator. Produces a real PDF (not a browser
 * page-print): a branded header (BI + title + meta lines), a "printed by …
 * + timestamp" line, a table body, and a per-page footer (brand + page #).
 *
 * Generic on purpose — every report page (Comisiones, Corte de caja,
 * Reportes, …) can call this with its own columns/rows. Keep page-specific
 * shaping (grouping, formatting) in the caller; this only renders.
 */
export interface PdfReportOptions {
  /** Document title, e.g. "Comisiones". */
  title: string;
  /** Lines under the title — date range, filters, attribution, etc. */
  meta?: string[];
  /** Who generated it (current username) — printed with the timestamp. */
  printedBy: string;
  /** Table header labels. */
  columns: string[];
  /** Body rows (already formatted by the caller). */
  rows: Array<Array<string | number>>;
  /** Column indices to right-align (numbers/money). */
  numericColumns?: number[];
  /** Body row indices to emphasize (e.g. per-group total rows). */
  emphasizeRows?: number[];
  /** Page orientation. Default 'portrait'; use 'landscape' for wide tables
   *  (e.g. the Corte de caja day/cashier pivots with many tender columns). */
  orientation?: 'portrait' | 'landscape';
  /** Download filename (should end in .pdf). */
  filename: string;
}

const BRAND: [number, number, number] = [37, 99, 235]; // tremor brand blue

/**
 * Builds the same branded report doc as exportReportPdf but returns the
 * jsPDF instance instead of saving it — the shared step both exportReportPdf
 * (triggers a download) and buildReportPdfBlob (JCD-23's "email this PDF",
 * attaches the identical bytes instead) build on.
 */
function buildReportPdfDoc(o: PdfReportOptions): jsPDF {
  const doc = new jsPDF({ orientation: o.orientation ?? 'portrait', unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 40;

  // ---- Header ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(17, 24, 39);
  doc.text(t.appName, margin, 44);
  doc.setFontSize(13);
  doc.text(o.title, margin, 62);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(75, 85, 99);
  let y = 78;
  for (const line of o.meta ?? []) { doc.text(line, margin, y); y += 12; }
  doc.text(`${t.print.printedBy}: ${o.printedBy} · ${new Date().toLocaleString('es-MX')}`, margin, y);
  doc.setTextColor(0, 0, 0);

  // ---- Body table ----
  const columnStyles: Record<number, { halign: 'right' }> = {};
  for (const i of o.numericColumns ?? []) columnStyles[i] = { halign: 'right' };
  const emph = new Set(o.emphasizeRows ?? []);

  autoTable(doc, {
    startY: y + 10,
    head: [o.columns],
    body: o.rows.map((r) => r.map((c) => String(c))),
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: BRAND, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [247, 248, 250] },
    columnStyles,
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      if (data.section === 'body' && emph.has(data.row.index)) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [233, 238, 245];
      }
    },
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`${t.appName} · ${new Date().toLocaleDateString('es-MX')}`, margin, pageH - 18);
      doc.text(String(doc.getCurrentPageInfo().pageNumber), pageW - margin, pageH - 18, { align: 'right' });
    },
  });

  return doc;
}

/** Renders the report and triggers a browser download (existing behavior). */
export function exportReportPdf(o: PdfReportOptions): void {
  buildReportPdfDoc(o).save(o.filename);
}

/**
 * Renders the report and returns it as a Blob instead of downloading it —
 * for attaching to an outgoing email (JCD-23) rather than saving to disk.
 * Byte-identical to what exportReportPdf would have saved.
 */
export function buildReportPdfBlob(o: PdfReportOptions): Blob {
  return buildReportPdfDoc(o).output('blob');
}
